import { t as uiText } from './i18n';
// src/App.tsx
import React, { useEffect, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { initTiming, detectVisitType } from './features/analytics/initTiming';
import { BrowserRouter as Router, Routes, Route, useParams, Navigate, useLocation, Outlet } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import Login from './pages/Login';
import AuthRedirect from './components/AuthRedirect';
import Home from './pages/Home';
import AccessDenied from './pages/AccessDenied';
import { AuthProvider } from './context/AuthContext';
import { UiLanguageSync } from './i18n/UiLanguageSync';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { SidebarProvider } from './context/SidebarContext';
import { HashtagProvider } from './context/HashtagContext';
import { SearchProvider } from './context/SearchContext';
import { ArchiveProvider } from './context/ArchiveContext';
import { BoardListProvider } from './context/BoardListContext';
import { FolderProvider } from './context/FolderContext';
import { PublicPageProvider } from './context/PublicPageContext';
import AccessControl from './components/AccessControl';
import ErrorBoundary from './components/ErrorBoundary';
import WhiteScreenDetector from './components/WhiteScreenDetector';
import WhiteScreenDetector2 from './components/WhiteScreenDetector2';
import { errorTracking } from './utils/errorTracking';
import { performanceMonitor } from './utils/performanceMonitor';
import { queryClient } from './lib/queryClient';
import QueryPerformancePanel from './components/QueryPerformancePanel';
import TestFloatingButton from './components/TestFloatingButton';
import UpdateBanner from './components/UpdateBanner';
import FeedbackPanelHost from './components/feedback/FeedbackPanelHost';
import StagingBadge from './components/StagingBadge';
import { shouldShowDevChrome } from './utils/embedContext';
import { enableConsoleBuffer } from './utils/consoleBuffer';
import { useWorkspaceTheme } from './theme/useWorkspaceTheme';
import { lazyWithRetry } from './utils/lazyWithRetry';
import AdminLegacyRedirect from './pages/AdminLegacyRedirect';
import AdminOrgRootRedirect from './pages/AdminOrgRootRedirect';
import AdminOpenCreateRedirect from './pages/AdminOpenCreateRedirect';
import { isAdminHost } from './utils/serviceAdminRoutes';
import { workspacePagePath } from './utils/workspaceNavigation';
// ネットワークfetchロガーを初期化
import './utils/fetchLogger';
// デバッグコマンドを開発環境でのみ読み込み
if (import.meta.env.MODE === 'development') {
  import('./utils/debugCommands');
}

const LazyAdminPage = lazyWithRetry(() => import('./pages/AdminPage'));
const LazyBillingPage = lazyWithRetry(() => import('./pages/BillingPage'));
const LazyServiceAdminLayout = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminLayout'));
const LazyServiceAdminDashboard = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminDashboard'));
const LazyServiceAdminWorkspaces = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminWorkspaces'));
const LazyServiceAdminWorkspaceDetail = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminWorkspaceDetail'));
const LazyServiceAdminCompanies = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminCompanies'));
const LazyServiceAdminCompanyDetail = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminCompanyDetail'));
const LazyServiceAdminUsers = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminUsers'));
const LazyServiceAdminErrors = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminErrors'));
const LazyServiceAdminFeedback = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminFeedback'));
const LazyServiceAdminOperators = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminOperators'));
const LazyServiceAdminOfferCatalog = lazyWithRetry(() => import('./pages/ServiceAdmin/ServiceAdminOfferCatalog'));
const LazyInviteLinkPage = lazyWithRetry(() => import('./pages/InviteLinkPage'));
const LazyOpenPage = lazyWithRetry(() => import('./pages/OpenPage'));
const LazyBoardEditor = lazyWithRetry(() => import('./pages/BoardEditor'));
const LazyHashtagDetail = lazyWithRetry(() => import('./pages/HashtagDetail'));
const LazyArchivedBoards = lazyWithRetry(() => import('./pages/ArchivedBoards'));
const LazyPublicPage = lazyWithRetry(() => import('./pages/PublicPage'));
const LazyPublicAISearchPage = lazyWithRetry(() => import('./pages/PublicAISearchPage'));
const LazyWidgetRAGPage = lazyWithRetry(() => import('./pages/WidgetRAGPage'));
const LazyWidgetEmbedPreviewPage = lazyWithRetry(() => import('./pages/WidgetEmbedPreviewPage'));
const LazyDemoPage = lazyWithRetry(() => import('./pages/DemoPage'));
const LazyNcgApplyPage = lazyWithRetry(() => import('./pages/NcgApplyPage'));

const RouteLoading: React.FC<{ label?: string }> = ({ label }) => {
  return (
    <div style={{ padding: 24 }}>
      <div style={{ fontSize: 14, color: '#666' }}>
        {uiText("Loading")}{label ? `: ${label}` : ''}...
      </div>
    </div>
  );
};

// ナビゲーションデバッグ有効化判定（開発時のみ）
function isNavDebugEnabled(): boolean {
  if (import.meta.env.MODE !== 'development') return false;
  if (typeof window === 'undefined') return false;
  try {
    const qs = new URLSearchParams(window.location.search);
    if (qs.get('debugNav') === '1') return true;
    return window.localStorage?.getItem('notreu.debugNav') === '1';
  } catch {
    return false;
  }
}

// ワークスペースルート用のラッパーコンポーネント
const WorkspaceRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { workspaceId } = useParams();
  return (
    <AccessControl workspaceId={workspaceId!}>
      {children}
    </AccessControl>
  );
};

/** ダッシュボード / ページ / スレッド。Home は親ルートのままマウントし、URL だけ切り替える。 */
const HomeTabPath: React.FC = () => null;

const WorkspaceHomeRoute: React.FC = () => {
  const { workspaceId } = useParams();
  return (
    <AccessControl workspaceId={workspaceId!}>
      <Home />
      <Outlet />
    </AccessControl>
  );
};

// ボードエディタルート用のラッパーコンポーネント
/** 旧 `/{workspaceId}/{slug}` を `/{workspaceId}/page/{slug}` へ寄せる。 */
const LegacyPagePathRedirect: React.FC = () => {
  const { workspaceId, boardId } = useParams();
  const location = useLocation();
  return (
    <Navigate
      to={{
        pathname: workspacePagePath(workspaceId || '', boardId || ''),
        search: location.search,
        hash: location.hash,
      }}
      replace
    />
  );
};

const BoardEditorRoute: React.FC<{ mode: 'new' | 'edit' }> = ({ mode }) => {
  const { workspaceId, boardId } = useParams();
  // 計測起点（ルート確定時）
  try {
    const hasLocal = typeof window !== 'undefined' && !!(window as any).indexedDB;
    const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    initTiming.start({
      workspaceId: workspaceId || 'unknown',
      boardId: boardId || 'unknown',
      visitType: detectVisitType(false /* 正確なIDB有無は後続で上書き */, isOnline)
    });
  } catch (_) {}
  return (
    <AccessControl workspaceId={workspaceId!}>
      <LazyBoardEditor key={`${workspaceId}-${boardId || 'new'}`} mode={mode} />
    </AccessControl>
  );
};

const WorkspaceThemeSync: React.FC = () => {
  useWorkspaceTheme();
  return null;
};

const ROOT_NOINDEX_META_ID = 'notreu-root-noindex';

/** ルート `/` のみ検索インデックス対象外（クライアント遷移で他パスへ来たときは noindex メタを外す） */
const RootPathNoindex: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    const isRoot = location.pathname === '/';
    const meta = document.getElementById(ROOT_NOINDEX_META_ID) as HTMLMetaElement | null;
    if (isRoot) {
      if (!meta) {
        const el = document.createElement('meta');
        el.id = ROOT_NOINDEX_META_ID;
        el.name = 'robots';
        el.content = 'noindex, nofollow';
        document.head.appendChild(el);
      }
    } else if (meta) {
      meta.remove();
    }
  }, [location.pathname]);
  return null;
};

const App: React.FC = () => {
  const { i18n } = useTranslation();
  const [, setUiLang] = useState(i18n.language);
  useEffect(() => {
    const onChange = (lng: string) => setUiLang(lng);
    i18n.on('languageChanged', onChange);
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [i18n]);

  const [showPerformancePanel, setShowPerformancePanel] = React.useState(false);

  // 開発環境でのみパフォーマンス監視パネルを表示
  useEffect(() => {
    // コンソールバッファを常時有効化（開発/テストビルド）
    enableConsoleBuffer();
    if (import.meta.env.MODE === 'development') {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.key === 'P') {
          setShowPerformancePanel(prev => !prev);
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  // Crashlytics初期化
  useEffect(() => {
    const initializeApp = async () => {
      const appStartTime = performance.now();

      let initTrace;
      try {
        initTrace = await performanceMonitor.startTrace('app_initialization');
        if (initTrace) {} else {
          console.warn('🚀 [App] Performance trace開始失敗: traceがnull', {
            timestamp: new Date().toISOString(),
            elapsedTime: performance.now() - appStartTime
          });
        }
      } catch (error) {
        console.warn('🚀 [App] Performance trace開始エラー:', {
          error,
          timestamp: new Date().toISOString(),
          elapsedTime: performance.now() - appStartTime
        });
      }

      try {
        errorTracking.init();
      } catch (error) {
        console.warn('🚀 [App] Error tracking初期化エラー:', {
          error,
          timestamp: new Date().toISOString(),
          elapsedTime: performance.now() - appStartTime
        });
      }

      try {
        performanceMonitor.init();
      } catch (error) {
        console.warn('🚀 [App] Performance monitor初期化エラー:', {
          error,
          timestamp: new Date().toISOString(),
          elapsedTime: performance.now() - appStartTime
        });
      }

      // iPhone固有の初期化ログ
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        try {
          errorTracking.logCustomError('iOS app initialization', {
            initialization_time: Date.now(),
            user_agent: navigator.userAgent,
            screen_width: window.screen.width,
            screen_height: window.screen.height,
            device_pixel_ratio: window.devicePixelRatio
          });
        } catch (error) {
          console.warn('🚀 [App] iOS初期化ログ送信エラー:', {
            error,
            timestamp: new Date().toISOString(),
            elapsedTime: performance.now() - appStartTime
          });
        }
      }

      // 初期化完了
      try {
        if (initTrace && typeof initTrace.stop === 'function') {
          initTrace.stop();
        } else {}
      } catch (error) {
        console.warn('🚀 [App] Performance trace停止エラー:', {
          error,
          timestamp: new Date().toISOString(),
          elapsedTime: performance.now() - appStartTime
        });
      }

      // 10秒後にパフォーマンスレポートを生成
      setTimeout(() => {
        try {
          performanceMonitor.generateReport();
        } catch (error) {
          console.warn('🚀 [App] パフォーマンスレポート生成エラー:', {
            error,
            timestamp: new Date().toISOString(),
            elapsedTime: performance.now() - appStartTime
          });
        }
      }, 10000);

      const finalTime = performance.now();
    };

    // 非同期初期化を実行
    initializeApp();
  }, []);

  return (
    <ErrorBoundary>
      <WhiteScreenDetector timeout={15000}>
        <WhiteScreenDetector2 />
        <QueryClientProvider client={queryClient}>
          <Router>
            <StagingBadge />
            <RootPathNoindex />
            <AuthProvider>
              <UiLanguageSync />
              <WorkspaceProvider>
                <UpdateBanner />
                <FeedbackPanelHost />
                <SidebarProvider>
                  <HashtagProvider>
                    <SearchProvider>
                      <ArchiveProvider>
                        <BoardListProvider>
                          <FolderProvider>
                            <WorkspaceThemeSync />
                            <Suspense fallback={<RouteLoading />}>
                              <Routes>
                                {/* ウィジェット専用ページ */}
                                <Route
                                  path="/widget/rag"
                                  element={
                                    <Suspense fallback={null}>
                                      <LazyWidgetRAGPage />
                                    </Suspense>
                                  }
                                />
                                <Route
                                  path="/widget/embed-preview"
                                  element={
                                    <Suspense fallback={null}>
                                      <LazyWidgetEmbedPreviewPage />
                                    </Suspense>
                                  }
                                />

                                {/* Staging 限定デモページ */}
                                <Route
                                  path="/demo"
                                  element={
                                    <Suspense fallback={null}>
                                      <LazyDemoPage />
                                    </Suspense>
                                  }
                                />
                                
                                {/* 公開ページ（匿名閲覧用） */}
                                <Route
                                  path="/cg/:publishId/component/notreu-ai-search"
                                  element={
                                    <PublicPageProvider>
                                      <Suspense fallback={null}>
                                        <LazyPublicAISearchPage />
                                      </Suspense>
                                    </PublicPageProvider>
                                  }
                                />
                                <Route
                                  path="/cg/:publishId"
                                  element={
                                    <PublicPageProvider>
                                      <Suspense fallback={null}>
                                        <LazyPublicPage />
                                      </Suspense>
                                    </PublicPageProvider>
                                  }
                                />
                                <Route
                                  path="/cg/:publishId/:slug"
                                  element={
                                    <PublicPageProvider>
                                      <Suspense fallback={null}>
                                        <LazyPublicPage />
                                      </Suspense>
                                    </PublicPageProvider>
                                  }
                                />
                                {/* 旧公開URL（互換維持しないが、workspaceルート誤判定を防ぐためNotFound扱い） */}
                                <Route path="/p/*" element={<div style={{ padding: 24 }}>404 Not Found</div>} />
                                {/* ログイン前 or リダイレクト判定。
                                    admin Hosting ではワークスペース URL に飛ばさず /dashboard に集約する。 */}
                                <Route
                                  path="/"
                                  element={
                                    isAdminHost() ? (
                                      <Navigate to="/dashboard" replace />
                                    ) : (
                                      <AuthRedirect />
                                    )
                                  }
                                />

                                {/* システム管理ページ */}
                                <Route path="/sys/login" element={<Login />} />
                                <Route
                                  path="/sys/ncg-apply"
                                  element={
                                    <Suspense fallback={<RouteLoading label={uiText("Sign up")} />}>
                                      <LazyNcgApplyPage />
                                    </Suspense>
                                  }
                                />
                                <Route path="/sys/access-denied" element={<AccessDenied />} />

                                {/* 運営側スーパー管理者向けコンソール（serviceAdmin 権限必須）
                                    admin Hosting (admin-notreu / admin-notreu-stg / admin.localhost) のみマウント。
                                    メイン Hosting からはマウントせず NotFound に倒して運営URLを露出させない。
                                    旧 /sys/admin/* プレフィックスは廃止し、ドメインルート直下にフラット展開。 */}
                                {isAdminHost() && (
                                <Route
                                  element={
                                    <Suspense fallback={<RouteLoading label={uiText("Operations console")} />}>
                                      <LazyServiceAdminLayout />
                                    </Suspense>
                                  }
                                >
                                  <Route
                                    path="/dashboard"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminDashboard />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/workspaces"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminWorkspaces />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/workspaces/:workspaceId"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminWorkspaceDetail />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/companies"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminCompanies />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/companies/:companyId"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminCompanyDetail />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/users"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminUsers />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/errors"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminErrors />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/feedback"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminFeedback />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/operators"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminOperators />
                                      </Suspense>
                                    }
                                  />
                                  <Route
                                    path="/offer-catalog"
                                    element={
                                      <Suspense fallback={<RouteLoading />}>
                                        <LazyServiceAdminOfferCatalog />
                                      </Suspense>
                                    }
                                  />
                                </Route>
                                )}

                                {/* admin Hosting 以外のみ: 通常の管理者画面 / ワークスペース系ルート。
                                    admin Hosting (admin.notreu.com など) では /dashboard 等の運営ルートと
                                    /sys/login / /sys/ncg-apply のみ提供し、ワークスペース URL を踏ませないようすべて 404 に倒す。 */}
                                {!isAdminHost() && (
                                  <>
                                    {/* 支払い管理（Stripe Customer Portal への中継） */}
                                    <Route
                                      path="/billing"
                                      element={
                                        <Suspense fallback={<RouteLoading label={uiText("Payment management")} />}>
                                          <LazyBillingPage />
                                        </Suspense>
                                      }
                                    />
                                    {/* 旧: 会社配下の請求画面 */}
                                    <Route path="/admin/org/:companyId/billing" element={<Navigate to="/billing" replace />} />
                                    <Route path="/admin/billing" element={<Navigate to="/billing" replace />} />

                                    {/* 管理者画面（企業は /admin/org/:companyId/:section） */}
                                    <Route path="/admin/create" element={<AdminOpenCreateRedirect />} />
                                    <Route
                                      path="/admin/org/:companyId/:section"
                                      element={
                                        <Suspense fallback={<RouteLoading label={uiText("Admin screen")} />}>
                                          <LazyAdminPage />
                                        </Suspense>
                                      }
                                    />
                                    <Route path="/admin/org/:companyId" element={<AdminOrgRootRedirect />} />
                                    <Route path="/admin" element={<AdminLegacyRedirect defaultSection="dashboard" />} />
                                    <Route path="/admin/:legacySection" element={<AdminLegacyRedirect />} />

                                    {/* ワークスペースがない場合のHome画面 */}
                                    <Route path="/home" element={<Home />} />

                                    {/* /open: LINE等のアプリ内ブラウザ対策の中継ページ（順序重要） */}
                                    <Route path="/open" element={<LazyOpenPage />} />

                                    {/* 招待リンク用ルート - InviteLinkPageで処理 */}
                                    <Route path="/:workspaceName/invite/:token" element={<LazyInviteLinkPage />} />

                                    {/* ワークスペース別ルート - 順序が重要 */}
                                    <Route path="/:workspaceId/new" element={<BoardEditorRoute mode="new" />} />

                                    <Route
                                      path="/:workspaceId/hashtags/:hashtagName"
                                      element={
                                        <WorkspaceRoute>
                                          <LazyHashtagDetail />
                                        </WorkspaceRoute>
                                      }
                                    />
                                    <Route
                                      path="/:workspaceId/archived"
                                      element={
                                        <WorkspaceRoute>
                                          <LazyArchivedBoards />
                                        </WorkspaceRoute>
                                      }
                                    />
                                    <Route path="/:workspaceId" element={<WorkspaceHomeRoute />}>
                                      <Route index element={<HomeTabPath />} />
                                      <Route path="dashboard" element={<HomeTabPath />} />
                                      <Route path="pages" element={<HomeTabPath />} />
                                      <Route path="threads" element={<HomeTabPath />} />
                                      <Route path="threads/:threadId" element={<HomeTabPath />} />
                                    </Route>
                                    <Route path="/:workspaceId/page/:boardId" element={<BoardEditorRoute mode="edit" />} />
                                    <Route path="/:workspaceId/:boardId" element={<LegacyPagePathRedirect />} />
                                  </>
                                )}

                                {/* admin Hosting 用の catch-all (NotFound)。
                                    /dashboard, /workspaces, /companies, /users, /errors, /operators
                                    （およびその子ルート）/ /sys/login / /sys/ncg-apply / /sys/access-denied / / 以外は 404。
                                    旧 /sys/admin/* もここで NotFound に倒れる（プレフィックス廃止のため）。 */}
                                {isAdminHost() && (
                                  <Route
                                    path="*"
                                    element={<div style={{ padding: 24 }}>404 Not Found</div>}
                                  />
                                )}
                              </Routes>
                            </Suspense>
                          </FolderProvider>
                        </BoardListProvider>
                      </ArchiveProvider>
                    </SearchProvider>
                  </HashtagProvider>
                </SidebarProvider>
              </WorkspaceProvider>
            </AuthProvider>
          </Router>
          {shouldShowDevChrome() && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
        {import.meta.env.MODE === 'development' && <TestFloatingButton />}
        {import.meta.env.MODE === 'development' && (
          <QueryPerformancePanel 
            isOpen={showPerformancePanel} 
            onClose={() => setShowPerformancePanel(false)} 
          />
        )}
      </WhiteScreenDetector>
    </ErrorBoundary>
  );
};

export default App;
